export type ReceiveTarget = { width: number; height: number; framerate: number; bitrate: number };
type DecoderSupport = { supported: boolean; smooth?: boolean; reason?: string };
type DecoderQuery = (config: any) => Promise<DecoderSupport>;
type OfferOptions = { codec?: string; target?: ReceiveTarget; conservative?: boolean; decodingInfo?: DecoderQuery };
// Explicit receive capacity, checked through MediaCapabilities before SDP is raised.
// These are receive targets, not commands to resize the shared camera.
export const defaultReceiveTarget = Object.freeze({ width: 1920, height: 1080, framerate: 144, bitrate: 8000000 });
const levels: Record<string, number[][]> = {
  h264: [
    [31, 3600, 108000, 14000000],
    [32, 5120, 216000, 20000000],
    [40, 8192, 245760, 20000000],
    [41, 8192, 245760, 50000000],
    [42, 8704, 522240, 50000000],
    [50, 22080, 589824, 135000000],
    [51, 36864, 983040, 240000000],
    [52, 36864, 2073600, 240000000],
  ],
  av1: [
    [5, 1065024, 31950720, 10000000],
    [8, 2359296, 70778880, 12000000],
    [9, 2359296, 141557760, 20000000],
    [12, 8912896, 267386880, 30000000],
    [13, 8912896, 534773760, 40000000],
  ],
};

export function validateReceiveTarget(target: ReceiveTarget) {
  if (
    !target ||
    !Number.isInteger(target.width) ||
    !Number.isInteger(target.height) ||
    target.width < 16 ||
    target.width > 3840 ||
    target.height < 16 ||
    target.height > 2160 ||
    !Number.isFinite(target.framerate) ||
    target.framerate < 1 ||
    target.framerate > 240 ||
    !Number.isInteger(target.bitrate) ||
    target.bitrate < 100000 ||
    target.bitrate > 40000000
  ) {
    throw new Error('Invalid receive target: use up to 3840×2160, 240 FPS and 40 Mbps.');
  }
  return { ...target };
}

function parameters(text: string) {
  const result = new Map();
  for (const item of text.split(';')) {
    const match = /^\s*([^=\s]+)\s*=\s*([^;]+?)\s*$/.exec(item);
    if (!match || result.has(match[1].toLowerCase())) return null;
    result.set(match[1].toLowerCase(), match[2]);
  }
  return result;
}

async function checkSupport(decodingInfo: DecoderQuery | undefined, video: Record<string, unknown>): Promise<DecoderSupport> {
  if (!decodingInfo) return { supported: false, reason: 'Decoder capability API unavailable' };
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve().then(() => decodingInfo({ type: 'webrtc', video: { ...video, spatialScalability: false } })),
      new Promise<DecoderSupport>((resolve) => {
        timer = setTimeout(() => resolve({ supported: false, reason: 'Decoder capability check timed out' }), 2000);
      }),
    ]);
  } catch (error) {
    return { supported: false, reason: error instanceof Error ? error.message : String(error) };
  } finally {
    clearTimeout(timer);
  }
}

/** Only matching payloads on receiving video sections are changed; ICE and other codecs remain intact. */
export async function prepareReceiverOffer(
  offer: RTCSessionDescriptionInit,
  {
    codec = 'h264',
    target = defaultReceiveTarget,
    decodingInfo = globalThis.navigator?.mediaCapabilities?.decodingInfo?.bind(globalThis.navigator.mediaCapabilities),
    conservative = false,
  }: OfferOptions = {},
) {
  target = validateReceiveTarget(target);
  if (conservative) target.framerate = Math.min(30, target.framerate);
  const pixels = codec === 'h264' ? Math.ceil(target.width / 16) * Math.ceil(target.height / 16) : target.width * target.height;
  const level = levels[codec]?.find(
    ([, size, rate, bitrate]) => pixels <= size && pixels * target.framerate <= rate && target.bitrate <= bitrate,
  )?.[0];
  const checks: { payloadType: number; configuration: ReceiveTarget & { contentType: string }; support: DecoderSupport }[] = [];
  const changes: { payloadType: number; from: string; to: string }[] = [];
  const lines = (offer.sdp ?? '').split('\r\n');
  for (let start = 0; start < lines.length; start++) {
    if (!/^m=video [1-9]\d* /.test(lines[start])) continue;
    let end = start + 1;
    while (end < lines.length && !lines[end].startsWith('m=')) end++;
    const section = lines.slice(start, end);
    if (section.some((l) => l === 'a=sendonly' || l === 'a=inactive')) {
      start = end - 1;
      continue;
    }
    const pts = new Set(lines[start].split(/\s+/).slice(3));
    const payloads = new Set(
      section.flatMap((line) => {
        const match = /^a=rtpmap:(\d+) (H264|AV1)\/90000$/i.exec(line);
        return match && match[2].toLowerCase() === codec && pts.has(match[1]) ? [match[1]] : [];
      }),
    );
    for (let i = start + 1; i < end; i++) {
      const match = /^a=fmtp:(\d+) (.+)$/.exec(lines[i]);
      if (level === undefined || !match || !payloads.has(match[1])) continue;
      const params = parameters(match[2]);
      if (!params) continue;
      let key: string, value: string, current: number;
      if (codec === 'h264') {
        const profile = params.get('profile-level-id');
        if (!/^42(?:e0|00)[0-9a-f]{2}$/i.test(profile ?? '') || params.get('packetization-mode') !== '1') continue;
        key = 'profile-level-id';
        current = parseInt(profile!.slice(4), 16);
        value = profile!.slice(0, 4) + level.toString(16).padStart(2, '0');
      } else {
        if ((params.get('profile') ?? '0') !== '0' || (params.get('tier') ?? '0') !== '0') continue;
        key = 'level-idx';
        current = Number(params.get(key) ?? 5);
        value = String(level);
      }
      // Conservative retry can also lower a previously high browser default.
      if (!conservative && current >= level) continue;
      const old = lines[i];
      params.set(key, value);
      const fmtp = [...params].map(([k, v]) => `${k}=${v}`).join(';');
      const contentType = `video/${codec === 'h264' ? 'H264' : 'AV1'};${fmtp}`;
      const support = await checkSupport(decodingInfo, { ...target, contentType });
      checks.push({ payloadType: Number(match[1]), configuration: { ...target, contentType }, support });
      if (support?.supported !== true || support?.smooth !== true) continue;
      lines[i] = `a=fmtp:${match[1]} ${fmtp}`;
      if (lines[i] !== old) changes.push({ payloadType: Number(match[1]), from: old, to: lines[i] });
    }
    start = end - 1;
  }
  return { offer: { ...offer, sdp: lines.join('\r\n') }, target, checks, changes, upgraded: changes.length > 0 };
}
